import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { LANDING_PROVIDER_BRANDS } from "../utils/providerIcons";
import {
  ArrowRight,
  Check,
  ChevronDown,
  FileText,
  CreditCard,
  BarChart3,
  QrCode,
  Bell,
  ShieldCheck,
  Mail,
  LayoutDashboard,
  Receipt,
  TrendingUp,
} from "lucide-react";

/* ========== SEO ========== */
const LANDING_TITLE = "NETIKASH FLOW | Facturation & Paiements Simplifiés";
const LANDING_DESCRIPTION =
    "Solution de facturation et de paiement pour entreprises. Factures illimitées, Wallet, Mobile Money, Visa. Essai 30 jours offert.";

const APP_ICON_SRC = `${process.env.PUBLIC_URL}/images/Logo_Netikash Flow - Logo Only 512x512.png`;
const HERO_IMAGE_SRC = `${process.env.PUBLIC_URL}/images/hero-flow.png`;

/* Taux indicatif pour affichage FC sur la landing (prix catalogue en USD) */
const USD_TO_CDF_DISPLAY = 2200;

const formatCdfApprox = (usd) => {
  if (usd == null || Number(usd) <= 0) return "0";
  return Math.round(Number(usd) * USD_TO_CDF_DISPLAY).toLocaleString("fr-FR");
};

/* ========== Données pricing ========== */
const PLANS = [
  {
    id: "gratuit",
    name: "Gratuit",
    priceMonthly: 0,
    priceYearly: 0,
    description: "Démarrer et tester",
    cta: "Commencer",
    featured: false,
    features: [
      "Factures créées illimitées",
      "3 factures normalisées",
      "2 500 USD/mois max",
      "1 organisation · 1 admin",
      "Wallet + Mobile + Visa",
      "Support email 48h",
    ],
  },
  {
    id: "standard",
    name: "Standard",
    priceMonthly: 8,
    priceYearly: 80,
    description: "Petites structures",
    cta: "Choisir Standard",
    featured: false,
    features: [
      "50 factures normalisées",
      "25 000 USD/mois max",
      "3 organisations · 5 users",
      "Email + WhatsApp",
      "Rappels email",
      "Export PDF · Support 24h",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    priceMonthly: 15,
    priceYearly: 150,
    description: "Équipes & croissance",
    cta: "Choisir Pro",
    featured: true,
    features: [
      "Factures normalisées illimitées",
      "150 000 USD/mois max",
      "10 orgs · 5 admins · users illimité",
      "Rappels Email + WhatsApp",
      "Export CSV + PDF · Webhooks",
      "Support email + tél + WhatsApp",
    ],
  },
  {
    id: "entreprise",
    name: "Entreprise",
    priceMonthly: 60,
    priceYearly: 600,
    description: "Grandes organisations",
    cta: "Nous contacter",
    featured: false,
    features: [
      "Tout illimité",
      "Insights avancés",
      "Gestionnaire de compte dédié",
    ],
  },
];

const FAQ_ITEMS = [
  {
    q: "Qu'est-ce que l'évaluation de 30 jours ?",
    a: "Vous bénéficiez d'une évaluation complète de 30 jours. Sans engagement. Si vous n'optez pour aucun package payant, votre compte passe sur le plan Gratuit. Aucune carte requise.",
  },
  {
    q: "Puis-je changer de plan plus tard ?",
    a: "Oui. Passez à un plan supérieur ou inférieur à tout moment. Les changements s'appliquent à la prochaine facturation.",
  },
  {
    q: "Les factures sont-elles conformes ?",
    a: "Les factures normalisées NETIKASH FLOW respectent les exigences en vigueur. Selon votre plan, nombre défini ou illimité.",
  },
  {
    q: "Paiements et retraits ?",
    a: "Vos clients paient par Wallet, Mobile Money et Visa. Vous retirez vers Mobile Money ou compte bancaire. Inclus dans tous les plans.",
  },
  {
    q: "Quel support est inclus ?",
    a: "Gratuit : email 48h. Standard : 24h. Pro : email, téléphone, WhatsApp. Entreprise : gestionnaire de compte dédié.",
  },
];

const FEATURES_ALTERNATING = [
  {
    title: "Facturation sans limite",
    body: "Créez autant de factures que nécessaire. Les factures normalisées et envoyées évoluent avec votre plan pour une conformité sans effort.",
    why: "Réduisez les erreurs et le temps passé sur l'administratif.",
    icon: FileText,
  },
  {
    title: "Encaisser partout",
    body: "Vos clients paient par Wallet, Mobile Money et Visa. Vous retirez vers Mobile Money ou banque — disponible sur tous les plans.",
    why: "Une trésorerie unifiée et des encaissements plus rapides.",
    icon: CreditCard,
  },
  {
    title: "Rappels & insights",
    body: "Rappels automatiques par email ou WhatsApp selon l'offre. Insights clients de Standard à Avancé pour mieux piloter.",
    why: "Moins d'impayés, meilleure connaissance client.",
    icon: BarChart3,
  },
];

const METRICS = [
  { value: "Illimité", label: "Factures créées" },
  { value: "99,9 %", label: "Disponibilité" },
  { value: "30 jours", label: "Essai offert" },
];

const TESTIMONIALS = [
  {
    quote:
        "Enfin factures et Mobile Money dans une seule plateforme. Gain de temps énorme.",
    author: "Responsable admin · PME · Lubumbashi",
  },
  {
    quote: "Les rappels auto ont fait chuter nos impayés. Trésorerie lisible.",
    author: "Gérant · Commerce · Kinshasa",
  },
  {
    quote: "Interface claire, support réactif. Migration en une journée.",
    author: "Fondateur · Startup · Goma",
  },
];

const PROVIDERS = LANDING_PROVIDER_BRANDS;

/* Carrousel de découverte interface */
const HERO_CAROUSEL = [
  {
    id: "pos",
    label: "Caissier",
    image: `${process.env.PUBLIC_URL}/images/caisse.png`,
    title: "Tableau de bord caissier",
    body: "Accès rapide aux encaissements, clients et transactions. Interface pensée pour la caisse.",
  },
  {
    id: "payment",
    label: "Demande de paiement",
    image: `${process.env.PUBLIC_URL}/images/payment-request.png`,
    title: "Demande de paiement",
    body: "Créez une demande, partagez le lien ou le QR. Suivi en temps réel du statut.",
  },
  {
    id: "invoice",
    label: "Factures",
    image: `${process.env.PUBLIC_URL}/images/Dashboard.png`,
    title: "Dashboard & facturation",
    body: "Vue d’ensemble, factures et encaissements. Tout centralisé pour piloter votre activité.",
  },
];

/* Animations Framer Motion */
const container = {
  hidden: { opacity: 0, y: 12 },
  show: {
    opacity: 1,
    y: 0,
    transition: { staggerChildren: 0.08, delayChildren: 0.15 },
  },
};

const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0 },
};

function FeatureIllustration({ variant = "invoice" }) {
  if (variant === "invoice") {
    return (
      <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-soft">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-white to-amber-100/60" />
        <div className="relative p-6 sm:p-7">
          <div className="flex items-center justify-between">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-200/70 bg-white/80 px-3 py-1 text-xs font-semibold text-amber-800 shadow-sm">
              <FileText className="h-3.5 w-3.5" />
              Facture
            </div>
            <div className="inline-flex items-center gap-2 text-xs text-slate-500">
              <ShieldCheck className="h-4 w-4 text-amber-600" />
              Conforme
            </div>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-soft">
              <p className="text-[11px] font-semibold text-slate-900">Client</p>
              <div className="mt-2 space-y-2">
                <div className="h-2 w-4/5 rounded-full bg-slate-200" />
                <div className="h-2 w-2/3 rounded-full bg-slate-200" />
              </div>
              <div className="mt-4 rounded-xl border border-amber-200/70 bg-amber-50/60 p-3">
                <p className="text-[11px] font-semibold text-amber-900">Total</p>
                <p className="mt-0.5 text-lg font-semibold tracking-tight text-slate-900">
                  125 <span className="text-sm font-medium text-slate-500">USD</span>
                </p>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-soft">
              <p className="text-[11px] font-semibold text-slate-900">Articles</p>
              <div className="mt-3 space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between gap-3">
                    <div className="h-2 w-2/3 rounded-full bg-slate-200" />
                    <div className="h-2 w-10 rounded-full bg-slate-200" />
                  </div>
                ))}
              </div>
              <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm">
                Envoyer au client
                <ArrowRight className="h-3.5 w-3.5" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (variant === "payment") {
    return (
      <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-soft">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-amber-100/50" />
        <div className="relative p-6 sm:p-7">
          <div className="flex items-center justify-between">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-3 py-1 text-xs font-semibold text-slate-800 shadow-sm">
              <QrCode className="h-3.5 w-3.5 text-amber-600" />
              Encaisser
            </div>
            <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true" />
              En attente
            </div>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-soft">
              <p className="text-[11px] font-semibold text-slate-900">Montant</p>
              <p className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
                125 <span className="text-base font-medium text-slate-500">USD</span>
              </p>
              <div className="mt-4 grid grid-cols-3 gap-2">
                {PROVIDERS.slice(0, 3).map((p) => (
                  <div key={p.name} className="flex items-center justify-center rounded-xl border border-slate-200 bg-white p-2">
                    <img src={p.src} alt={p.name} className="h-5 w-5 object-contain" loading="lazy" />
                  </div>
                ))}
              </div>
              <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] font-semibold text-amber-900">
                Lien & QR
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-soft">
              <p className="text-[11px] font-semibold text-slate-900">QR Code</p>
              <div className="mt-3 grid place-items-center">
                <div className="rounded-2xl bg-white p-3 ring-1 ring-slate-900/5 shadow-sm">
                  <div className="grid grid-cols-7 gap-[3px]" aria-hidden="true">
                    {Array.from({ length: 49 }).map((_, i) => (
                      <span
                        key={i}
                        className={`block h-2 w-2 rounded-[2px] ${
                          i % 3 === 0 || i % 5 === 0 ? "bg-slate-900" : "bg-slate-100"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <p className="mt-3 text-[11px] text-slate-500">Scan → paiement → statut mis à jour</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-soft">
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/60 via-white to-amber-100/40" />
      <div className="relative p-6 sm:p-7">
        <div className="flex items-center justify-between">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-3 py-1 text-xs font-semibold text-slate-800 shadow-sm">
            <BarChart3 className="h-3.5 w-3.5 text-amber-600" />
            Insights
          </div>
          <div className="inline-flex items-center gap-2 text-xs text-slate-500">
            <Bell className="h-4 w-4 text-amber-600" />
            Alertes
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-soft">
            <p className="text-[11px] font-semibold text-slate-900">Encaissements</p>
            <div className="mt-4 flex h-24 items-end gap-2">
              {[40, 70, 55, 85, 60, 78, 92].map((h, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-t bg-gradient-to-t from-amber-500/70 to-emerald-400/50"
                  style={{ height: `${h}%` }}
                />
              ))}
            </div>
            <p className="mt-2 text-[11px] text-slate-500">Tendance sur 7 jours</p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-soft">
            <p className="text-[11px] font-semibold text-slate-900">Santé</p>
            <div className="mt-3 space-y-3">
              {[
                { l: "Taux succès", v: "98%", c: "bg-emerald-500" },
                { l: "En attente", v: "3%", c: "bg-amber-500" },
                { l: "Échecs", v: "−1%", c: "bg-rose-500" },
              ].map((k) => (
                <div key={k.l} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${k.c}`} aria-hidden="true" />
                    <span className="text-[11px] font-semibold text-slate-800">{k.l}</span>
                  </div>
                  <span className="text-[11px] font-semibold text-slate-900">{k.v}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 rounded-xl border border-amber-200/70 bg-amber-50/60 p-3">
              <p className="text-[11px] font-semibold text-amber-900">Recommandation</p>
              <p className="mt-0.5 text-[11px] text-slate-600">Relance automatique pour réduire les impayés.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const location = useLocation();
  const [faqOpen, setFaqOpen] = useState(null);
  const [pricingYearly, setPricingYearly] = useState(false);
  const [heroPreview, setHeroPreview] = useState("pos");
  const [navOpen, setNavOpen] = useState(false);
  const shouldReduceMotion = useReducedMotion();

  // Si OAuth renvoie vers / (redirect_uri racine), transférer vers /login qui gère le code
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("code") || params.get("error")) {
      navigate(`/login${location.search}`, { replace: true });
    }
  }, [location.search, navigate]);
  const qrPattern = [
    // 1 = noir (sombre), 0 = vide (clair) — QR stylisé non scannable
    "111111111111111",
    "100000100010001",
    "101110101010111",
    "101110100010111",
    "101110101110111",
    "100000100000001",
    "111111101111111",
    "000100001001000",
    "111011111101011",
    "001000100000100",
    "110111011110111",
    "000010000010000",
    "111111101111111",
    "100000100000001",
    "111111111111111",
  ];

  /* SEO */
  useEffect(() => {
    document.title = LANDING_TITLE;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", LANDING_DESCRIPTION);
  }, []);

  /* Auto-cycle carrousel découverte */
  useEffect(() => {
    if (shouldReduceMotion) return;
    const order = ["pos", "payment", "invoice"];
    const id = window.setInterval(() => {
      setHeroPreview((prev) => order[(order.indexOf(prev) + 1) % order.length]);
    }, 4800);
    return () => window.clearInterval(id);
  }, [shouldReduceMotion]);

  return (
      <div className="min-h-screen bg-white text-slate-900 antialiased">
        {/* Fond grid + noise global */}
        <div
            className="fixed inset-0 pointer-events-none z-0 opacity-[0.4]"
            style={{
              backgroundImage: `
            linear-gradient(rgba(0,0,0,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,0,0,0.02) 1px, transparent 1px)
          `,
              backgroundSize: "32px 32px",
            }}
        />
        <div
            className="fixed inset-0 pointer-events-none z-0 opacity-[0.018]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            }}
        />

        {/* ========== NAV ========== */}
        <header className="fixed top-0 left-0 right-0 z-50 border-b border-amber-200/50 bg-white/90 shadow-sm backdrop-blur-xl">
          <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-amber-400 to-transparent opacity-90" />
          <nav className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
            <Link
                to="/"
                className="flex items-center gap-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400/50 focus:ring-offset-2"
            >
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 shadow-md ring-1 ring-amber-500/20">
                <img
                    src={APP_ICON_SRC}
                    alt="Netikash Flow"
                    className="h-7 w-7 object-contain drop-shadow-sm"
                    loading="eager"
                />
              </div>
              <span className="font-semibold text-slate-900">
              <span className="text-amber-600">NETIKASH</span>
              <span className="text-slate-700"> FLOW</span>
            </span>
            </Link>

            {/* Desktop nav */}
            <div className="hidden items-center gap-6 sm:flex">
              <a
                  href="#features"
                  className="text-sm text-slate-500 hover:text-amber-600 transition-colors duration-200"
              >
                Fonctionnalités
              </a>
              <a
                  href="#pricing"
                  className="text-sm text-slate-500 hover:text-amber-600 transition-colors duration-200"
              >
                Tarifs
              </a>
              <a
                  href="#faq"
                  className="text-sm text-slate-500 hover:text-amber-600 transition-colors duration-200"
              >
                FAQ
              </a>
              <Link
                  to="/login"
                  className="rounded-full bg-gradient-to-r from-amber-500 to-amber-600 px-4 py-2 text-sm font-medium text-white shadow-md hover:from-amber-600 hover:to-amber-700 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
              >
                Connexion
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
                type="button"
                className="inline-flex items-center justify-center rounded-lg p-2 text-slate-600 hover:text-amber-600 hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-400/60 focus:ring-offset-2 sm:hidden"
                aria-label="Ouvrir le menu de navigation"
                aria-expanded={navOpen}
                onClick={() => setNavOpen((open) => !open)}
            >
              <span className="sr-only">Ouvrir le menu</span>
              <div className="space-y-1">
                <span className={`block h-0.5 w-5 rounded-full bg-current transition-transform ${navOpen ? "translate-y-1.5 rotate-45" : ""}`} />
                <span className={`block h-0.5 w-5 rounded-full bg-current transition-opacity ${navOpen ? "opacity-0" : ""}`} />
                <span className={`block h-0.5 w-5 rounded-full bg-current transition-transform ${navOpen ? "-translate-y-1.5 -rotate-45" : ""}`} />
              </div>
            </button>
          </nav>

          {/* Mobile nav panel */}
          {navOpen && (
              <div className="sm:hidden border-t border-slate-200 bg-white/95 backdrop-blur-md">
                <div className="mx-auto max-w-6xl px-4 py-3 space-y-2">
                  <a
                      href="#features"
                      onClick={() => setNavOpen(false)}
                      className="block rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-amber-50 hover:text-amber-700"
                  >
                    Fonctionnalités
                  </a>
                  <a
                      href="#pricing"
                      onClick={() => setNavOpen(false)}
                      className="block rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-amber-50 hover:text-amber-700"
                  >
                    Tarifs
                  </a>
                  <a
                      href="#faq"
                      onClick={() => setNavOpen(false)}
                      className="block rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-amber-50 hover:text-amber-700"
                  >
                    FAQ
                  </a>
                  <Link
                      to="/login"
                      onClick={() => setNavOpen(false)}
                      className="mt-1 inline-flex w-full items-center justify-center rounded-full bg-gradient-to-r from-amber-500 to-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-md hover:from-amber-600 hover:to-amber-700 hover:shadow-lg"
                  >
                    Connexion
                  </Link>
                </div>
              </div>
          )}
        </header>

        {/* ========== HERO ========== */}
        <section
            id="hero"
            className="relative z-10 pt-28 pb-20 sm:pt-36 sm:pb-28 lg:pt-44 lg:pb-32 overflow-hidden"
            aria-labelledby="hero-heading"
        >
          {/* Glows amber subtils */}
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-amber-400/25 blur-3xl" />
            <div className="absolute bottom-[-6rem] right-[-4rem] h-80 w-80 rounded-full bg-amber-500/20 blur-3xl" />
          </div>

          <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <motion.div
                className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16"
                initial="hidden"
                animate="show"
                variants={container}
            >
              {/* Colonne gauche : texte parfaitement stacké */}
              <div className="flex flex-col items-start gap-8">
                <div className="space-y-4">
                  <motion.h1
                      id="hero-heading"
                      variants={item}
                      className="text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl xl:text-7xl"
                  >
                    <span className="block">Facturez et encaissez</span>
                    <span className="mt-1 block bg-gradient-to-r from-brand-700 via-amber-600 to-brand-600 bg-[length:220%_220%] bg-clip-text text-transparent animate-gradient-x">
                    sans friction.
                  </span>
                  </motion.h1>
                  <motion.p
                      variants={item}
                      className="max-w-xl text-base text-slate-600 sm:text-lg"
                  >
                    NETIKASH FLOW centralise facturation, liens de paiement, Mobile
                    Money, Visa et wallet. Créez une facture, envoyez un lien ou un
                    QR, suivez chaque paiement en temps réel.
                  </motion.p>
                </div>

                {/* CTA stack (parfaitement aligné) */}
                <motion.div
                    variants={item}
                    className="flex w-full flex-col gap-3 sm:flex-row sm:items-center"
                >
                  <Link
                      to="/login"
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 px-7 py-3.5 text-sm sm:text-base font-semibold text-white shadow-glow hover:from-amber-600 hover:to-amber-700 hover:shadow-soft-xl hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 sm:w-auto"
                  >
                    Démarrer l’essai
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                  <a
                      href="#discover-interface"
                      className="inline-flex w-full items-center justify-center rounded-full border border-slate-200 bg-white px-7 py-3.5 text-sm sm:text-base font-medium text-slate-700 hover:border-amber-300 hover:text-amber-800 hover:bg-amber-50/60 transition-all duration-200 sm:w-auto"
                  >
                    Voir la démo
                  </a>
                </motion.div>

                {/* Metrics alignés */}
                <motion.div
                    variants={item}
                    className="flex flex-wrap gap-4"
                    aria-label="Indicateurs clés"
                >
                  {METRICS.map((m) => (
                      <div
                          key={m.label}
                          className="inline-flex items-center gap-2 rounded-full border border-amber-100 bg-white/80 px-3 py-2 text-xs sm:text-sm shadow-sm"
                      >
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-50">
                      <Check className="h-3.5 w-3.5 text-amber-600" />
                    </span>
                        <div className="flex flex-col leading-tight">
                      <span className="font-semibold text-amber-700">
                        {m.value}
                      </span>
                          <span className="text-[11px] text-slate-500">
                        {m.label}
                      </span>
                        </div>
                      </div>
                  ))}
                </motion.div>

                {/* Providers alignés en bas de la colonne */}
                <motion.div
                    variants={item}
                    className="mt-2 flex flex-col gap-3"
                    aria-label="Moyens de paiement supportés"
                >
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
                    Encaissements via
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    {PROVIDERS.slice(0, 5).map((p) => (
                        <div
                            key={p.name}
                            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/90 px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm"
                        >
                          <img
                              src={p.src}
                              alt={p.name}
                              className="h-4 w-4 object-contain"
                              loading="lazy"
                          />
                          <span>{p.name}</span>
                        </div>
                    ))}
                  </div>
                </motion.div>
              </div>

              {/* Colonne droite : breathing space + blob abstrait */}
              <motion.div
                  variants={item}
                  className="relative flex h-full items-center justify-center"
                  aria-hidden="true"
              >
                <div className="relative aspect-[4/5] w-full max-w-md rounded-[2.5rem] border border-amber-100/80 bg-gradient-to-br from-amber-50 via-white to-amber-100/60 shadow-soft-xl overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-radial from-amber-300/25 via-transparent to-transparent blur-3xl" />
                  <div className="relative flex h-full flex-col items-center justify-center gap-5 px-6 py-8">
                    <div className="rounded-full bg-white/85 px-4 py-2 text-xs font-medium text-amber-800 shadow-sm border border-amber-100">
                      Pensé pour les équipes finance & terrain
                    </div>

                    <div className="relative mt-2 w-full max-w-sm rounded-2xl border border-white/60 bg-white/70 shadow-soft overflow-hidden aspect-square">
                      <img
                          src={HERO_IMAGE_SRC}
                          alt="Commerçante utilisant Netikash Flow pour encaisser via tablette et QR code"
                          className="h-full w-full object-cover object-center"
                          loading="eager"
                          fetchPriority="high"
                      />
                    </div>

                    <div className="max-w-xs text-center">
                      <p className="text-xs font-semibold text-slate-800">
                        Le paiement, réinventé pour votre commerce.
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        Un simple QR code Flow permet à vos clients de payer instantanément, rapidement et en toute sécurité.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ========== DÉCOUVERTE INTERFACE (carrousel) ========== */}
        <section
            id="discover-interface"
            className="relative z-10 border-t border-slate-100 bg-slate-50/60 py-16 sm:py-20"
            aria-labelledby="discover-heading"
        >
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center mb-8 sm:mb-10">
              <h2
                  id="discover-heading"
                  className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl"
              >
                Découvrez l’interface en action
              </h2>
              <p className="mt-3 text-sm sm:text-base text-slate-600">
                Passez d’un tableau de bord caissier à une demande de paiement puis à la
                facturation normalisée, sans changer d’outil.
              </p>
            </div>

            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-soft-xl ring-1 ring-slate-900/5">
              {/* Tabs carrousel */}
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-slate-50/80 px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                    <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
                    <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
                  </div>
                  <span className="ml-3 text-xs text-slate-500">
                  Aperçu produit · Flow Web
                </span>
                </div>
                <span className="hidden text-[11px] font-medium text-slate-500 sm:inline-flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                Temps réel
              </span>
              </div>

              <div className="p-3 sm:p-6 space-y-4 sm:space-y-5">
                {/* Onglets */}
                <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white p-1.5 shadow-sm">
                  {HERO_CAROUSEL.map((p) => (
                      <button
                          key={p.id}
                          type="button"
                          onClick={() => setHeroPreview(p.id)}
                          className={`flex-1 rounded-xl px-3 py-2 text-xs sm:text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/70 focus-visible:ring-offset-1 ${
                              heroPreview === p.id
                                  ? "bg-slate-900 text-white shadow-sm"
                                  : "text-slate-600 hover:bg-amber-50/70 hover:text-amber-800"
                          }`}
                          aria-pressed={heroPreview === p.id}
                          aria-label={`Voir l’interface ${p.label}`}
                      >
                        {p.label}
                      </button>
                  ))}
                </div>

                {/* Zone carrousel (images) */}
                <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-50/70 shadow-soft snap-x snap-mandatory max-h-[70vh] sm:max-h-[420px] flex items-center justify-center">
                  <AnimatePresence mode="wait" initial={false}>
                    {HERO_CAROUSEL.filter((p) => p.id === heroPreview).map((p) => (
                        <motion.div
                            key={p.id}
                            initial={shouldReduceMotion ? undefined : { opacity: 0, x: 24, scale: 0.97 }}
                            animate={shouldReduceMotion ? { opacity: 1 } : { opacity: 1, x: 0, scale: 1 }}
                            exit={shouldReduceMotion ? undefined : { opacity: 0, x: -24, scale: 0.97 }}
                            transition={{ duration: 0.35, ease: "easeOut" }}
                            className="relative w-full snap-center"
                        >
                          <img
                              src={p.image}
                              alt={p.title}
                              className="h-full w-full rounded-2xl object-contain object-top bg-white"
                              loading="lazy"
                          />
                          <div className="pointer-events-none absolute inset-x-0 bottom-0 rounded-b-2xl bg-gradient-to-t from-slate-900/85 via-slate-900/40 to-transparent p-4 pt-8">
                            <p className="text-sm sm:text-base font-semibold text-white">
                              {p.title}
                            </p>
                            <p className="mt-0.5 text-xs sm:text-sm text-white/90">
                              {p.body}
                            </p>
                          </div>
                        </motion.div>
                    ))}
                  </AnimatePresence>

                  {/* Bullets carrousel */}
                  <div className="pointer-events-auto absolute inset-x-0 bottom-2 flex justify-center gap-1.5">
                    {HERO_CAROUSEL.map((p) => (
                        <button
                            key={p.id}
                            type="button"
                            onClick={() => setHeroPreview(p.id)}
                            className={`h-1.5 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/80 focus-visible:ring-offset-1 ${
                                heroPreview === p.id
                                    ? "w-5 bg-amber-500"
                                    : "w-1.5 bg-slate-300 hover:bg-slate-400"
                            }`}
                            aria-label={`Aller à la slide ${p.label}`}
                        />
                    ))}
                  </div>
                </div>

                {/* Mini mock dashboard statique sous le carrousel */}
                <div className="grid gap-4 sm:grid-cols-[1.2fr_0.8fr] items-start mt-2">
                  <div className="rounded-2xl border border-slate-200 bg-slate-900 text-slate-50 p-4 sm:p-5 shadow-soft">
                    <div className="flex items-center justify-between text-xs text-slate-300">
                      <span>Vue d’ensemble encaissements</span>
                      <span className="inline-flex items-center gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      Live
                    </span>
                    </div>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-[11px] sm:text-xs">
                      {[
                        { l: "Chiffre d’affaires", v: "12 450 $" },
                        { l: "Factures envoyées", v: "128" },
                        { l: "Taux d’encaissement", v: "98 %" },
                      ].map((c) => (
                          <div
                              key={c.l}
                              className="rounded-xl bg-slate-800/70 p-3 border border-slate-700/70"
                          >
                            <p className="text-[11px] text-slate-400">{c.l}</p>
                            <p className="mt-1 text-sm font-semibold text-white">
                              {c.v}
                            </p>
                          </div>
                      ))}
                    </div>
                    <div className="mt-4 h-16 rounded-xl bg-gradient-to-tr from-amber-500/60 via-amber-300/40 to-emerald-400/50" />
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-soft text-xs sm:text-sm text-slate-600">
                    <p className="font-semibold text-slate-900 mb-1.5">
                      Un seul outil pour caissiers, admins et direction
                    </p>
                    <p>
                      Le web app reprend les flux du POS mobile : choix de
                      l’organisation, mode caissier ou admin, et suivi transverse
                      des paiements. Aucune double saisie, aucune perte
                      d’information.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========== SOCIAL PROOF ==========
          (sections suivantes inchangées) */}
        <section
            id="social-proof"
            className="relative z-10 border-t border-amber-100/60 bg-slate-50/50 py-16 sm:py-20"
            aria-labelledby="social-heading"
        >
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <p
                id="social-heading"
                className="text-center text-sm font-medium uppercase tracking-wider text-amber-600/80"
            >
              Des équipes qui encaissent déjà avec Flow
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-6 sm:gap-10">
              {PROVIDERS.map((p) => (
                  <motion.div
                      key={p.name}
                      className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-soft"
                      whileHover={shouldReduceMotion ? undefined : { y: -2 }}
                  >
                    <img
                        src={p.src}
                        alt={p.name}
                        className="h-7 w-7 object-contain"
                        loading="lazy"
                    />
                    <div className="text-left">
                      <p className="text-sm font-semibold text-slate-800">
                        {p.name}
                      </p>
                      <p className="text-xs text-slate-500">Canal de paiement</p>
                    </div>
                  </motion.div>
              ))}
            </div>
            <div className="mt-20 grid gap-8 sm:grid-cols-3">
              {TESTIMONIALS.map((t, i) => (
                  <motion.blockquote
                      key={i}
                      initial={{ opacity: 0, y: 12 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                      className="relative rounded-2xl border border-slate-100 bg-white p-6 shadow-soft overflow-hidden"
                  >
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-400 to-amber-500 rounded-l" />
                    <p className="text-slate-600 pl-2">"{t.quote}"</p>
                    <footer className="mt-4 text-sm text-slate-500 pl-2">
                      — {t.author}
                    </footer>
                  </motion.blockquote>
              ))}
            </div>
            <div className="mt-12 flex flex-wrap justify-center gap-12 text-center">
              {METRICS.map((m, i) => (
                  <motion.div
                      key={i}
                      whileHover={{ scale: 1.05 }}
                      className="cursor-default"
                  >
                <span className="block text-2xl font-semibold text-amber-700">
                  {m.value}
                </span>
                    <span className="text-sm text-slate-500">{m.label}</span>
                  </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ========== FEATURES ==========
          (inchangée) */}
        <section
            id="features"
            className="relative z-10 py-20 sm:py-28"
            aria-labelledby="features-heading"
        >
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <h2
                  id="features-heading"
                  className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl"
              >
                Tout pour facturer et encaisser sereinement
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                Fonctionnalités pensées pour la productivité et la trésorerie.
              </p>
            </div>
            <div className="mt-16 space-y-24">
              {FEATURES_ALTERNATING.map((feat, i) => (
                  <motion.div
                      key={feat.title}
                      initial={{ opacity: 0, y: 24 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      className={`grid gap-12 lg:grid-cols-2 lg:gap-16 items-center ${
                          i % 2 === 1 ? "lg:flex-row-reverse" : ""
                      }`}
                  >
                    <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                      <motion.div
                          className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100 text-amber-600 shadow-sm"
                          whileHover={{ scale: 1.05, rotate: 2 }}
                          transition={{ type: "spring", stiffness: 400, damping: 20 }}
                      >
                        <feat.icon className="h-6 w-6" />
                      </motion.div>
                      <h3 className="mt-4 text-2xl font-semibold text-slate-900">
                        {feat.title}
                      </h3>
                      <p className="mt-3 text-slate-600">{feat.body}</p>
                      <div className="mt-6 rounded-xl border border-amber-200/60 bg-amber-50/70 p-4">
                        <p className="text-sm font-medium text-amber-800">
                          Pourquoi c'est important
                        </p>
                        <p className="mt-1 text-sm text-amber-700/90">
                          {feat.why}
                        </p>
                      </div>
                    </div>
                    <motion.div
                        className={`${i % 2 === 1 ? "lg:order-1" : ""}`}
                        whileHover={shouldReduceMotion ? undefined : { y: -2 }}
                        transition={{ type: "spring", stiffness: 260, damping: 22 }}
                    >
                      <FeatureIllustration
                          variant={i === 0 ? "invoice" : i === 1 ? "payment" : "analytics"}
                      />
                    </motion.div>
                  </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ========== PRODUCT EXPERIENCE (inchangée) ========== */}
        <section
            id="product"
            className="relative z-10 border-t border-slate-100 bg-slate-50/50 py-20 sm:py-28"
            aria-labelledby="product-heading"
        >
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <h2
                  id="product-heading"
                  className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl"
              >
                Une expérience produit pensée pour vous
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                Interface claire, données en temps réel, tout au même endroit.
              </p>
            </div>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mt-14 overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-soft-xl"
            >
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-100">
                {[
                  {
                    icon: LayoutDashboard,
                    label: "Dashboard",
                    desc: "Vue d'ensemble en un coup d'œil",
                  },
                  {
                    icon: Receipt,
                    label: "Factures",
                    desc: "Création et envoi conformes",
                  },
                  {
                    icon: CreditCard,
                    label: "Paiements",
                    desc: "Wallet, Mobile Money, Visa",
                  },
                  {
                    icon: TrendingUp,
                    label: "Rapports",
                    desc: "Exports et insights",
                  },
                ].map((block) => (
                    <motion.div
                        key={block.label}
                        className="group flex flex-col items-center bg-white p-8 text-center"
                        whileHover={{
                          backgroundColor: "rgba(255,251,235,0.5)",
                        }}
                        transition={{ duration: 0.2 }}
                    >
                      <div className="rounded-2xl bg-amber-50 p-4 transition-colors duration-200 group-hover:bg-amber-100">
                        <block.icon className="h-8 w-8 text-amber-500 transition-colors group-hover:text-amber-600" />
                      </div>
                      <h3 className="mt-4 font-semibold text-slate-900">
                        {block.label}
                      </h3>
                      <p className="mt-1 text-sm text-slate-500">{block.desc}</p>
                    </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* ========== PRICING ========== */}
        <section
            id="pricing"
            className="relative z-10 py-20 sm:py-28"
            aria-labelledby="pricing-heading"
        >
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <h2
                  id="pricing-heading"
                  className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl"
              >
                Tarifs simples
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                <strong className="text-slate-800">Par utilisateur / mois</strong>
                . Évaluation 30 jours offerte.
              </p>
              <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-amber-200/70 bg-amber-50/50 p-1">
                <button
                    type="button"
                    onClick={() => setPricingYearly(false)}
                    className={`rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 ${
                        !pricingYearly
                            ? "bg-amber-500 text-white shadow-md"
                            : "text-slate-600 hover:text-amber-700"
                    }`}
                >
                  Mensuel
                </button>
                <button
                    type="button"
                    onClick={() => setPricingYearly(true)}
                    className={`rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 ${
                        pricingYearly
                            ? "bg-amber-500 text-white shadow-md"
                            : "text-slate-600 hover:text-amber-700"
                    }`}
                >
                  Annuel
                </button>
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700 font-semibold">
                −17 %
              </span>
              </div>
            </div>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {PLANS.map((plan) => {
                const price =
                    pricingYearly && plan.priceYearly != null
                        ? plan.priceYearly
                        : plan.priceMonthly;
                const period = pricingYearly ? "an" : "mois";
                return (
                    <motion.div
                        key={plan.id}
                        whileHover={{ y: plan.featured ? -6 : -2 }}
                        transition={{ type: "spring", stiffness: 300, damping: 25 }}
                        className={`relative flex flex-col rounded-3xl border-2 bg-white p-6 transition-colors duration-200 ${
                            plan.featured
                                ? "border-amber-500 shadow-lg ring-2 ring-amber-500/20"
                                : "border-slate-200 hover:border-amber-200"
                        }`}
                    >
                      {plan.featured && (
                          <motion.span
                              initial={{ scale: 0.9, opacity: 0.9 }}
                              animate={{ scale: 1, opacity: 1 }}
                              className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 px-3 py-1 text-xs font-medium text-white shadow-md"
                          >
                            Recommandé
                          </motion.span>
                      )}
                      <h3 className="font-semibold text-slate-900">{plan.name}</h3>
                      <p className="mt-1 text-sm text-slate-500">
                        {plan.description}
                      </p>
                      <div className="mt-6 space-y-1">
                        <div className="flex flex-wrap items-baseline gap-x-1 gap-y-0">
                          <span className="text-3xl font-semibold tracking-tight text-slate-900">
                            {price === 0 ? "0" : price}
                          </span>
                          {price > 0 && (
                            <>
                              <span className="text-xl font-medium text-slate-600">USD</span>
                            </>
                          )}
                        </div>
                        {price > 0 && (
                          <p className="text-sm font-medium text-amber-800/90">
                            ≈ {formatCdfApprox(price)} FC
                          </p>
                        )}
                        {price === 0 && (
                          <p className="text-sm text-slate-500">Gratuit</p>
                        )}
                        <p className="text-xs text-slate-500 leading-snug pt-1">
                          / utilisateur / {period}
                        </p>
                      </div>
                      <ul className="mt-6 flex-1 space-y-3 text-sm text-slate-600">
                        {plan.features.map((f, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <Check className="h-4 w-4 flex-shrink-0 text-brand-600 mt-0.5" />
                              {f}
                            </li>
                        ))}
                      </ul>
                      <Link
                          to="/login"
                          className={`mt-6 block w-full rounded-full py-3 text-center text-sm font-medium transition-all duration-200 ${
                              plan.featured
                                  ? "bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-md hover:from-amber-600 hover:to-amber-700 hover:shadow-lg hover:-translate-y-0.5"
                                  : "border-2 border-slate-200 text-slate-700 hover:border-amber-300 hover:bg-amber-50/50 hover:text-amber-800"
                          }`}
                      >
                        {plan.cta}
                      </Link>
                    </motion.div>
                );
              })}
            </div>
            <p className="mt-8 text-center text-sm text-slate-500">
              Sans engagement. Passage sur le plan Gratuit si aucun package payant
              après l&apos;évaluation.
            </p>
          </div>
        </section>

        {/* ========== FAQ (inchangée) ========== */}
        <section
            id="faq"
            className="relative z-10 border-t border-slate-100 bg-slate-50/50 py-20 sm:py-28"
            aria-labelledby="faq-heading"
        >
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <h2
                id="faq-heading"
                className="text-center text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl"
            >
              Questions fréquentes
            </h2>
            <div className="mt-12 space-y-2">
              {FAQ_ITEMS.map((faq, i) => (
                  <motion.div
                      key={i}
                      layout
                      className="rounded-2xl border border-slate-200/80 bg-white shadow-soft overflow-hidden"
                  >
                    <button
                        type="button"
                        onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                        className={`flex w-full items-center justify-between gap-4 px-5 py-4 text-left font-medium transition-colors duration-200 rounded-2xl ${
                            faqOpen === i
                                ? "text-amber-800 bg-amber-50/60"
                                : "text-slate-900 hover:bg-amber-50/30"
                        }`}
                        aria-expanded={faqOpen === i}
                    >
                      {faq.q}
                      <motion.span
                          animate={{ rotate: faqOpen === i ? 180 : 0 }}
                          className={`flex-shrink-0 transition-colors ${
                              faqOpen === i ? "text-amber-600" : "text-slate-400"
                          }`}
                      >
                        <ChevronDown className="h-5 w-5" />
                      </motion.span>
                    </button>
                    <AnimatePresence initial={false}>
                      {faqOpen === i && (
                          <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.25, ease: "easeInOut" }}
                              className="overflow-hidden"
                          >
                            <p className="border-t border-amber-100 px-5 py-4 text-slate-600 bg-white">
                              {faq.a}
                            </p>
                          </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ========== CTA FINAL (inchangée) ========== */}
        <section
            id="cta-final"
            className="relative z-10 py-20 sm:py-28 overflow-hidden"
            aria-labelledby="cta-heading"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-amber-50/40 via-white to-white pointer-events-none" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] rounded-full bg-amber-200/20 blur-[120px] pointer-events-none" />
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8 relative">
            <h2
                id="cta-heading"
                className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl"
            >
              Prêt à simplifier votre facturation ?
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Rejoignez les équipes qui facturent et encaissent avec sérénité.
            </p>
            <div className="mt-10">
              <Link
                  to="/login"
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 px-8 py-4 text-base font-medium text-white shadow-lg hover:from-amber-600 hover:to-amber-700 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
              >
                Commencer gratuitement
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <p className="mt-5 text-sm text-slate-500">
              Essai 30 jours · Aucune carte requise
            </p>
          </div>
        </section>

        {/* ========== FOOTER (inchangée) ========== */}
        <footer className="relative z-10 border-t border-amber-100/70 bg-gradient-to-b from-slate-50 to-amber-50/20 py-16">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <Link
                    to="/"
                    className="font-semibold text-slate-900 hover:opacity-90 transition-opacity"
                >
                  <span className="text-amber-600">NETIKASH</span> FLOW
                </Link>
                <p className="mt-3 text-sm text-slate-500">
                  Facturation et paiements pour les entreprises.
                </p>
              </div>
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Produit
                </h3>
                <ul className="mt-4 space-y-2 text-sm text-slate-600">
                  <li>
                    <a
                        href="#features"
                        className="hover:text-amber-600 transition-colors duration-200"
                    >
                      Fonctionnalités
                    </a>
                  </li>
                  <li>
                    <a
                        href="#pricing"
                        className="hover:text-amber-600 transition-colors duration-200"
                    >
                      Tarifs
                    </a>
                  </li>
                  <li>
                    <a
                        href="#faq"
                        className="hover:text-amber-600 transition-colors duration-200"
                    >
                      FAQ
                    </a>
                  </li>
                  <li>
                    <Link
                        to="/login"
                        className="hover:text-amber-600 transition-colors duration-200"
                    >
                      Connexion
                    </Link>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Légal
                </h3>
                <ul className="mt-4 space-y-2 text-sm text-slate-600">
                  <li>
                    <Link
                        to="/about"
                        className="hover:text-amber-600 transition-colors duration-200"
                    >
                      À propos
                    </Link>
                  </li>
                  <li>
                    <Link
                        to="/contact"
                        className="hover:text-amber-600 transition-colors duration-200"
                    >
                      Contact
                    </Link>
                  </li>
                  <li>
                    <Link
                        to="/faq"
                        className="hover:text-amber-600 transition-colors duration-200"
                    >
                      FAQ
                    </Link>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Contact
                </h3>
                <div className="mt-4 flex gap-4">
                  <a
                      href="#"
                      className="text-slate-400 hover:text-amber-600 transition-colors duration-200"
                      aria-label="Email"
                  >
                    <Mail className="h-5 w-5" />
                  </a>
                </div>
              </div>
            </div>
            <div className="mt-12 border-t border-slate-200/80 pt-8 text-center text-sm text-slate-500">
              © {new Date().getFullYear()} NETIKASH FLOW. Tous droits réservés.
            </div>
          </div>
        </footer>
      </div>
  );
}